import { DocumentFilterSearchType } from "../../schemas/boolean_filtering";
import { DocumentType } from "../../schemas/enums";
import { WEAVIATE_INDEXED_FIELDS } from "../../schemas/document";

/**
 * Represents a single filter condition.
 * Multiple ColumnFilters can target the same column for AND logic.
 */
export interface ColumnFilter {
  id: string;           // Unique filter ID (uuid)
  columnId: string;     // Backend field name, e.g., "title", "authors", "body"
  value: string;
  searchType: DocumentFilterSearchType;
}

/**
 * The complete multi-filter state for the grid.
 */
export interface MultiFilterState {
  filters: ColumnFilter[];
}

/**
 * Column metadata for the filter UI.
 */
export interface FilterableColumn {
  id: string;           // Backend field name
  label: string;        // Display label
  type: 'text' | 'date' | 'enum';
  enumValues?: string[];  // For enum-type columns like DocumentType
}

/**
 * All filterable columns in the DocumentGrid.
 * Maps to backend field names.
 */
export const FILTERABLE_COLUMNS: FilterableColumn[] = [
  { id: 'title', label: 'Title', type: 'text' },
  { id: 'summary', label: 'Summary', type: 'text' },
  { id: 'body', label: 'Body', type: 'text' },
  { id: 'file_name', label: 'File Name', type: 'text' },
  { id: 'participants', label: 'Participants', type: 'text' },
  { id: 'type', label: 'Document Type', type: 'enum', enumValues: Object.values(DocumentType) },
  { id: 'file_created_date', label: 'File Created', type: 'date' },
  { id: 'file_modified_date', label: 'File Modified', type: 'date' },
  { id: 'content_date_start', label: 'Content Date Start', type: 'date' },
  { id: 'content_date_end', label: 'Content Date End', type: 'date' },
  // Email-specific fields
  { id: 'from_email', label: 'From', type: 'text' },
  { id: 'to_email', label: 'To', type: 'text' },
  { id: 'x_from', label: 'X-From', type: 'text' },
  { id: 'x_to', label: 'X-To', type: 'text' },
  { id: 'x_cc', label: 'CC', type: 'text' },
  { id: 'x_bcc', label: 'BCC', type: 'text' },
];

/**
 * Search type options for the filter UI.
 */
export const SEARCH_TYPE_OPTIONS: { value: DocumentFilterSearchType; label: string }[] = [
  { value: 'CONTAINS', label: 'Contains' },
  { value: 'EXACT', label: 'Exact Match' },
  { value: 'STARTS_WITH', label: 'Starts With' },
  { value: 'ENDS_WITH', label: 'Ends With' },
  { value: 'DOES_NOT_CONTAIN', label: 'Does Not Contain' },
  { value: 'REGEX', label: 'Regex' },
  { value: 'FUZZY', label: 'Fuzzy' },
];

/**
 * Date-specific search type options.
 */
export const DATE_SEARCH_TYPE_OPTIONS: { value: DocumentFilterSearchType; label: string }[] = [
  { value: 'DATE_BEFORE', label: 'Before' },
  { value: 'DATE_AFTER', label: 'After' },
  { value: 'EXACT', label: 'Exact Date' },
];

/** Semantic search option — only shown for columns with Weaviate embeddings */
const SIMILAR_TO_OPTION: { value: DocumentFilterSearchType; label: string } = {
  value: 'SIMILAR_TO', label: 'Similar To (AI)',
};

/**
 * Get the appropriate search type options based on column type.
 * Columns with Weaviate embeddings (body, summary, title) also get "Similar To".
 */
export function getSearchTypeOptionsForColumn(column: FilterableColumn): { value: DocumentFilterSearchType; label: string }[] {
  if (column.type === 'date') {
    return DATE_SEARCH_TYPE_OPTIONS;
  }
  const base = [...SEARCH_TYPE_OPTIONS];
  if (WEAVIATE_INDEXED_FIELDS.has(column.id)) {
    base.unshift(SIMILAR_TO_OPTION);
  }
  return base;
}

/**
 * Get the default search type for a column type.
 */
export function getDefaultSearchType(column: FilterableColumn): DocumentFilterSearchType {
  if (column.type === 'date') {
    return 'DATE_AFTER';
  }
  if (column.type === 'enum') {
    return 'EXACT';
  }
  return 'CONTAINS';
}

/**
 * Find a filterable column by ID.
 */
export function getFilterableColumn(columnId: string): FilterableColumn | undefined {
  return FILTERABLE_COLUMNS.find(c => c.id === columnId);
}